import { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Project, TableColumn, SubfieldDefinition } from '../../../types/domain';
import { apiClient } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';

interface UseSchemaStateProps {
  table: Table;
  project: Project;
  canEdit: boolean;
  columns?: TableColumn[];
  onColumnsUpdated?: (columns: TableColumn[]) => void;
  onTableUpdated: (table: Table) => void;
}

export function useSchemaState({
  table,
  project,
  canEdit,
  columns,
  onColumnsUpdated,
  onTableUpdated,
}: UseSchemaStateProps) {
  const { showToast } = useToast();

  const [schemaMode, setSchemaMode] = useState<'dynamic' | 'strict'>(table.schema_mode || 'dynamic');
  const [isSavingSchemaMode, setIsSavingSchemaMode] = useState(false);

  // Column Schema State (Local working copy vs Baseline persisted copy)
  const [columnList, setColumnList] = useState<TableColumn[]>(columns || []);
  const [baselineColumns, setBaselineColumns] = useState<TableColumn[]>(columns || []);
  const [deletedColIds, setDeletedColIds] = useState<string[]>([]);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const [searchFilter, setSearchFilter] = useState('');
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    (columns || []).forEach((c) => {
      if (
        c.data_type === 'object' ||
        c.data_type === 'array' ||
        (c.validation_rules?.subfields && c.validation_rules.subfields.length > 0)
      ) {
        initial.add(c.id);
      }
    });
    return initial;
  });

  // Column Deletion Confirmation Modal State
  const [colToDelete, setColToDelete] = useState<TableColumn | null>(null);

  // Drag and Drop reordering state
  const [draggedColId, setDraggedColId] = useState<string | null>(null);

  // Sync schema mode when table changes
  useEffect(() => {
    setSchemaMode(table.schema_mode || 'dynamic');
  }, [table.id, table.schema_mode]);

  // Compute dirty/changed status
  const hasChanges = useMemo(() => {
    if (deletedColIds.length > 0) return true;
    if (columnList.length !== baselineColumns.length) return true;
    return JSON.stringify(columnList) !== JSON.stringify(baselineColumns);
  }, [columnList, baselineColumns, deletedColIds]);

  // Sync columns from parent if baseline hasn't been locally modified
  useEffect(() => {
    if (columns && !hasChanges) {
      setColumnList(columns);
      setBaselineColumns(columns);
    }
  }, [columns, hasChanges]);

  // Warn user before navigating away with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const handleSaveSchemaMode = async (newMode: 'dynamic' | 'strict') => {
    if (!canEdit) return;
    setSchemaMode(newMode);
    setIsSavingSchemaMode(true);
    try {
      const res = await apiClient<any>(`/projects/${project.id}/tables/${table.id}`, {
        method: 'PUT',
        body: JSON.stringify({ schema_mode: newMode }),
      });

      const updated = res.table || res || { ...table, schema_mode: newMode };
      showToast({
        type: 'success',
        title: 'Schema Evaluation Mode Updated',
        message: `Datastore '${table.name}' set to ${newMode.toUpperCase()} schema mode.`,
      });
      onTableUpdated(updated);
    } catch (err: any) {
      setSchemaMode(table.schema_mode || 'dynamic');
      showToast({
        type: 'error',
        title: 'Failed to update schema mode',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSavingSchemaMode(false);
    }
  };

  // Local mutations without auto-save
  const handleUpdateField = (colId: string, field: keyof TableColumn, value: any) => {
    setColumnList((prev) =>
      prev.map((col) => {
        if (col.id !== colId) return col;
        const updated: TableColumn = { ...col, [field]: value };

        // Auto slug key if changing name and key hasn't been manually decoupled
        if (field === 'name') {
          const generatedSlug = (value as string)
            .toLowerCase()
            .replace(/[^a-z0-9_]+/g, '_')
            .replace(/^_+|_+$/g, '');
          const prevDefaultSlug = col.name.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
          if (col.key === '' || col.key === prevDefaultSlug) {
            updated.key = generatedSlug;
          }
        }

        // Auto-expand if switched to object or array
        if (field === 'data_type') {
          const isObjOrArr = value === 'object' || value === 'array';
          if (isObjOrArr) {
            setExpandedRowIds((prevExp) => new Set([...prevExp, colId]));
          }
          if (value !== 'array' && updated.validation_rules?.max_items) {
            const rules = { ...updated.validation_rules };
            delete rules.max_items;
            updated.validation_rules = rules;
          }
        }

        return updated;
      })
    );
  };

  const handleUpdateValidationRules = useCallback((colId: string, patch: Record<string, any>) => {
    setColumnList((prev) =>
      prev.map((col) => {
        if (col.id !== colId) return col;
        const updatedRules = { ...(col.validation_rules || {}), ...patch };
        Object.keys(updatedRules).forEach((k) => {
          if (updatedRules[k] === undefined || updatedRules[k] === '') {
            delete updatedRules[k];
          }
        });
        return {
          ...col,
          validation_rules: updatedRules,
        };
      })
    );
  }, []);

  const handleUpdateSampleValue = (colId: string, sampleValue: string) => {
    handleUpdateValidationRules(colId, { sample_value: sampleValue });
  };

  const handleToggleRequired = (colId: string) => {
    const col = columnList.find((c) => c.id === colId);
    if (!col) return;
    handleUpdateField(col.id, 'is_required', !col.is_required);
  };

  // Subfield local mutations
  const handleAddSubfield = (colId: string) => {
    const col = columnList.find((c) => c.id === colId);
    if (!col) return;

    const subfields = [...(col.validation_rules?.subfields || [])];
    const newIdx = subfields.length + 1;
    const newSub: SubfieldDefinition = {
      id: crypto.randomUUID ? crypto.randomUUID() : `sub_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      name: `Field ${newIdx}`,
      key: `field_${newIdx}`,
      data_type: 'text',
      is_required: true,
      is_nullable: true,
      sample_value: '',
    };

    setColumnList((prev) =>
      prev.map((c) =>
        c.id === colId
          ? {
              ...c,
              validation_rules: {
                ...(c.validation_rules || {}),
                subfields: [...subfields, newSub],
              },
            }
          : c
      )
    );
    setExpandedRowIds((prev) => new Set([...prev, colId]));
  };

  const handleUpdateSubfield = (colId: string, subfieldId: string, field: keyof SubfieldDefinition, value: any) => {
    setColumnList((prev) =>
      prev.map((col) => {
        if (col.id !== colId) return col;
        const subfields = (col.validation_rules?.subfields || []).map((sub) => {
          if (sub.id !== subfieldId) return sub;
          const nextSub = { ...sub, [field]: value };
          if (field === 'name') {
            const slug = (value as string).toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
            const prevDefault = sub.name.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
            if (sub.key === '' || sub.key === prevDefault) {
              nextSub.key = slug;
            }
          }
          return nextSub;
        });
        return {
          ...col,
          validation_rules: {
            ...(col.validation_rules || {}),
            subfields,
          },
        };
      })
    );
  };

  const handleDeleteSubfield = (colId: string, subfieldId: string) => {
    setColumnList((prev) =>
      prev.map((col) => {
        if (col.id !== colId) return col;
        const subfields = (col.validation_rules?.subfields || []).filter((s) => s.id !== subfieldId);
        return {
          ...col,
          validation_rules: {
            ...(col.validation_rules || {}),
            subfields,
          },
        };
      })
    );
  };

  // Add new Top-Level Attribute (Local draft with temp_ ID, placed on right)
  const handleAddNewAttribute = () => {
    if (!canEdit) return;

    const nextPos = columnList.length;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const newCol: TableColumn = {
      id: tempId,
      table_id: table.id,
      name: `Attribute ${nextPos + 1}`,
      key: `attribute_${nextPos + 1}`,
      data_type: 'text',
      is_required: false,
      is_nullable: true,
      default_value: undefined,
      is_visible: true,
      position: nextPos,
      width: 180,
      validation_rules: {},
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    setColumnList((prev) => [...prev, newCol]);
  };

  // Delete column stage confirmation
  const confirmDeleteColumn = () => {
    if (!colToDelete) return;
    if (!colToDelete.id.startsWith('temp_')) {
      setDeletedColIds((prev) => [...prev, colToDelete.id]);
    }
    setColumnList((prev) => prev.filter((c) => c.id !== colToDelete.id));
    setColToDelete(null);
  };

  // Apply schema modifications originating from Advanced JSON Mode
  const handleApplyFromAdvancedJson = (newCols: TableColumn[]) => {
    setColumnList(newCols);
    const newExpanded = new Set(expandedRowIds);
    newCols.forEach((c) => {
      if (
        c.data_type === 'object' ||
        c.data_type === 'array' ||
        (c.validation_rules?.subfields && c.validation_rules.subfields.length > 0)
      ) {
        newExpanded.add(c.id);
      }
    });
    setExpandedRowIds(newExpanded);
    showToast({
      type: 'success',
      title: 'Schema Updated from JSON',
      message: `Applied ${newCols.length} attributes to the table grid. Click "Save Changes" to persist.`,
    });
  };

  // Discard all pending local modifications
  const handleDiscardChanges = () => {
    setColumnList(baselineColumns);
    setDeletedColIds([]);
    showToast({
      type: 'info',
      title: 'Changes Discarded',
      message: 'Reverted all unsaved modifications back to saved schema.',
    });
  };

  // Explicit Save Changes button action with comprehensive defensive validation
  const handleSaveChanges = async () => {
    if (!canEdit || isSavingChanges || !hasChanges) return;

    // Client-side validation: Check names, keys, and duplicate keys
    const seenKeys = new Set<string>();
    for (let i = 0; i < columnList.length; i++) {
      const col = columnList[i];
      const trimmedName = col.name.trim();
      const trimmedKey = col.key.trim().toLowerCase();

      if (!trimmedName) {
        showToast({
          type: 'warning',
          title: 'Missing Field Label',
          message: `Field at row ${i + 1} is missing a label.`,
        });
        return;
      }
      if (!trimmedKey) {
        showToast({
          type: 'warning',
          title: 'Missing Field Key',
          message: `Field '${trimmedName}' is missing a key.`,
        });
        return;
      }
      if (seenKeys.has(trimmedKey)) {
        showToast({
          type: 'warning',
          title: 'Duplicate Field Key',
          message: `Field key '${trimmedKey}' is used more than once. Each column must have a unique key.`,
        });
        return;
      }
      seenKeys.add(trimmedKey);
    }

    setIsSavingChanges(true);

    try {
      // 1. Delete removed columns on server
      const successfulDeletions: string[] = [];
      for (const colId of deletedColIds) {
        if (!colId.startsWith('temp_')) {
          await apiClient(`/projects/${project.id}/tables/${table.id}/columns/${colId}`, {
            method: 'DELETE',
          });
          successfulDeletions.push(colId);
        }
      }
      setDeletedColIds((prev) => prev.filter((id) => !successfulDeletions.includes(id)));

      // 2. Create new columns and update existing columns with accurate positions
      const finalColumns: TableColumn[] = [];
      const updatedExpanded = new Set(expandedRowIds);

      for (let i = 0; i < columnList.length; i++) {
        const col = { ...columnList[i], position: i };
        if (col.id.startsWith('temp_')) {
          const oldTempId = col.id;
          const created = await apiClient<TableColumn>(`/projects/${project.id}/tables/${table.id}/columns`, {
            method: 'POST',
            body: JSON.stringify({
              name: col.name.trim(),
              key: col.key.trim().toLowerCase(),
              data_type: col.data_type,
              is_required: col.is_required,
              is_nullable: col.is_nullable,
              default_value: col.default_value || null,
              is_visible: col.is_visible ?? true,
              position: i,
              width: col.width || 180,
              validation_rules: col.validation_rules || {},
            }),
          });
          finalColumns.push(created);

          // Update expanded set if the temporary row was expanded
          if (updatedExpanded.has(oldTempId)) {
            updatedExpanded.delete(oldTempId);
            updatedExpanded.add(created.id);
          }
        } else {
          const updated = await apiClient<TableColumn>(
            `/projects/${project.id}/tables/${table.id}/columns/${col.id}`,
            {
              method: 'PUT',
              body: JSON.stringify(col),
            }
          );
          finalColumns.push(updated);
        }
      }

      setExpandedRowIds(updatedExpanded);
      setColumnList(finalColumns);
      setBaselineColumns(finalColumns);
      onColumnsUpdated?.(finalColumns);

      showToast({
        type: 'success',
        title: 'Schema Saved',
        message: 'All schema columns and subfields have been saved successfully.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Save Failed',
        message: err?.message || 'Failed to save schema changes',
      });
    } finally {
      setIsSavingChanges(false);
    }
  };

  // Expand / Collapse controls
  const toggleRowExpand = (id: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    const all = new Set<string>();
    columnList.forEach((c) => all.add(c.id));
    setExpandedRowIds(all);
  };

  const handleCollapseAll = () => {
    setExpandedRowIds(new Set());
  };

  // Reorder Drag & Drop (Local reorder without auto-save)
  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedColId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedColId || draggedColId === targetId || !canEdit) {
      setDraggedColId(null);
      return;
    }

    const fromIdx = columnList.findIndex((c) => c.id === draggedColId);
    const toIdx = columnList.findIndex((c) => c.id === targetId);
    if (fromIdx === -1 || toIdx === -1) {
      setDraggedColId(null);
      return;
    }

    const reordered = [...columnList];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    const withPositions = reordered.map((col, idx) => ({ ...col, position: idx }));
    setColumnList(withPositions);
    setDraggedColId(null);
  };

  // Filtered rows matching search query
  const filteredColumns = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    if (!q) return columnList;

    return columnList.filter((col) => {
      const matchName = col.name?.toLowerCase().includes(q);
      const matchKey = col.key?.toLowerCase().includes(q);
      const matchType = col.data_type?.toLowerCase().includes(q);
      const subfields = col.validation_rules?.subfields || [];
      const matchSub = subfields.some(
        (s) => s.name?.toLowerCase().includes(q) || s.key?.toLowerCase().includes(q) || s.data_type?.toLowerCase().includes(q)
      );
      return matchName || matchKey || matchType || matchSub;
    });
  }, [columnList, searchFilter]);

  return {
    schemaMode,
    isSavingSchemaMode,
    handleSaveSchemaMode,
    columnList,
    baselineColumns,
    hasChanges,
    isSavingChanges,
    searchFilter,
    setSearchFilter,
    expandedRowIds,
    toggleRowExpand,
    handleExpandAll,
    handleCollapseAll,
    colToDelete,
    setColToDelete,
    confirmDeleteColumn,
    draggedColId,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleUpdateField,
    handleUpdateValidationRules,
    handleUpdateSampleValue,
    handleToggleRequired,
    handleAddSubfield,
    handleUpdateSubfield,
    handleDeleteSubfield,
    handleAddNewAttribute,
    handleApplyFromAdvancedJson,
    handleDiscardChanges,
    handleSaveChanges,
    filteredColumns,
  };
}
